import React from 'react'
import { Youtube, Play, Share2, ThumbsUp, Eye, Book, ExternalLink } from 'lucide-react'

interface YouTubePreviewContentProps {
  data: {
    title?: string
    description?: string
    target_url?: string
    bookTitle?: string
    author?: string
    isbn?: string
    coverUrl?: string
    autoplay?: boolean
    fonts?: {
      heading?: string
      body?: string
    }
    textSizes?: {
      bookTitle?: string
      description?: string
      branding?: string
    }
    companyPosition?: 'top' | 'bottom'
  }
  branding: {
    companyName?: string
    textColor?: string
    primaryColor?: string
    secondaryColor?: string
    brandingType?: 'beyondpages' | 'custom' | 'none'
    wrapperColor?: string
    wrapperOpacity?: number
  }
  bgGradient?: string
  colorMode?: 'gradient' | 'structured'
  isPreview?: boolean
}

export const YouTubePreviewContent: React.FC<YouTubePreviewContentProps> = ({ 
  data, 
  branding,
  bgGradient = 'linear-gradient(to bottom right, #DC2626, #991B1B, #000000)',
  colorMode = 'gradient',
  isPreview = true
}) => {
  // Extract YouTube video ID from URL
  const getYouTubeId = (url: string) => {
    if (!url) return null
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/)
    return match ? match[1] : null
  }

  const videoId = getYouTubeId(data.target_url || '')
  
  // Main content component
  const MainContent = () => (
    <>
      {/* Company name at top */}
      {branding.brandingType !== 'none' && data.companyPosition === 'top' && (
        <div className="text-center mb-4 px-4">
          {branding.brandingType === 'beyondpages' ? (
            <p className="text-sm opacity-80" style={{ 
              color: branding.textColor || 'white',
              fontFamily: data.fonts?.body || 'Inter' 
            }}>
              Powered by BeyondPages
            </p>
          ) : branding.brandingType === 'custom' && branding.companyName ? (
            <h2 className={`${data.textSizes?.branding || 'text-lg'} font-semibold`} style={{ 
              color: branding.textColor || 'white',
              fontFamily: data.fonts?.body || 'Inter' 
            }}>
              {branding.companyName}
            </h2>
          ) : null}
        </div>
      )}

      {/* Book Information Header (65% area) */}
      <div className="px-4 pb-8">
        {(data.bookTitle || data.coverUrl) && (
          <div className="flex gap-6 items-start max-w-sm mx-auto">
            {/* Book Cover */}
            {data.coverUrl && (
              <div className="flex-shrink-0">
                <img 
                  src={data.coverUrl} 
                  alt={data.bookTitle || 'Buchcover'}
                  className="w-24 h-36 object-cover rounded-lg shadow-lg"
                  style={{
                    border: colorMode === 'structured' ? '2px solid white' : 'none'
                  }}
                />
              </div>
            )}
            
            {/* Book Details */}
            <div className="flex-1">
              {data.bookTitle && (
                <h1 className={`${data.textSizes?.bookTitle || 'text-2xl'} font-bold mb-1`} style={{ 
                  color: branding.textColor || 'white',
                  fontFamily: data.fonts?.heading || 'Inter'
                }}>
                  {data.bookTitle}
                </h1>
              )}
              {data.author && (
                <p className="text-lg mb-2" style={{ 
                  color: branding.textColor || 'white',
                  opacity: 0.9,
                  fontFamily: data.fonts?.body || 'Inter'
                }}>
                  von {data.author}
                </p>
              )}
              {data.isbn && (
                <p className="text-sm" style={{ 
                  color: branding.textColor || 'white',
                  opacity: 0.7,
                  fontFamily: data.fonts?.body || 'Inter'
                }}>
                  ISBN: {data.isbn}
                </p>
              )}
            </div>
          </div>
        )}
        
        {/* Description */}
        {data.description && (
          <div className="max-w-sm mx-auto mt-6">
            <p className={`${data.textSizes?.description || 'text-base'} leading-relaxed text-center`} style={{ 
              color: branding.textColor || 'white',
              opacity: 0.9,
              fontFamily: data.fonts?.body || 'Inter'
            }}>
              {data.description}
            </p>
          </div>
        )}
      </div>

      {/* YouTube Player Section (35% area) */}
      <div className="px-4 pb-4">
        <div className="max-w-sm mx-auto">
          {/* Video Player */}
          <div className="bg-black rounded-lg overflow-hidden shadow-xl mb-4 relative" style={{ paddingBottom: '56.25%' }}>
            {videoId ? (
              <iframe
                src={`https://www.youtube.com/embed/${videoId}${data.autoplay ? '?autoplay=1' : ''}`}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                <div className="text-center">
                  <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Play className="w-10 h-10 text-white ml-1" />
                  </div>
                  <p className="text-white text-sm">
                    YouTube Video
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Action Button */}
          <a
            href={data.target_url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center px-4 py-3 rounded-full font-semibold shadow-lg transition-transform hover:scale-105 no-underline"
            style={{ 
              backgroundColor: colorMode === 'structured' ? 'white' : branding.secondaryColor || '#DC2626',
              color: colorMode === 'structured' ? (branding.secondaryColor || '#1F2937') : (branding.textColor || 'white')
            }}
          >
            <span className="flex items-center justify-center gap-2">
              <Youtube className="w-5 h-5" />
              Auf YouTube ansehen
              <ExternalLink className="w-4 h-4" />
            </span>
          </a>

          {/* Company name at bottom */}
          {branding.brandingType !== 'none' && data.companyPosition === 'bottom' && (
            <div className="text-center mt-6">
              {branding.brandingType === 'beyondpages' ? (
                <p className="text-sm opacity-80" style={{ 
                  color: colorMode === 'structured' ? (branding.secondaryColor || '#1F2937') : (branding.textColor || 'white'),
                  fontFamily: data.fonts?.body || 'Inter' 
                }}>
                  Powered by BeyondPages
                </p>
              ) : branding.brandingType === 'custom' && branding.companyName ? (
                <h2 className={`${data.textSizes?.branding || 'text-lg'} font-semibold`} style={{ 
                  color: colorMode === 'structured' ? (branding.secondaryColor || '#1F2937') : (branding.textColor || 'white'),
                  fontFamily: data.fonts?.body || 'Inter' 
                }}>
                  {branding.companyName}
                </h2>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </>
  )

  return (
    <div className={`h-full flex flex-col bg-gray-50 ${isPreview ? 'pt-14 sm:pt-4' : 'pt-0'}`}>
      {/* Content */}
      <div className="flex-1 overflow-y-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {colorMode === 'gradient' ? (
          // Gradient mode - single continuous gradient
          <div className="min-h-full relative" style={{
            background: bgGradient
          }}>
            {/* Animated background particles */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-red-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
              <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-black rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
            </div>
            
            {/* Content layer */}
            <div className="relative z-10 pt-12">
              <MainContent />
            </div>
          </div>
        ) : (
          // Structured mode - 65/35 split
          <div className="min-h-full relative">
            {/* Fixed background split */}
            <div className="absolute inset-0 flex flex-col">
              <div style={{ backgroundColor: branding.primaryColor, height: '65%' }} />
              <div className="flex-1" style={{ backgroundColor: branding.secondaryColor }} />
            </div>
            
            {/* Content layer */}
            <div className="relative z-10 pt-12">
              <MainContent />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}